import { useEffect, useRef, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Avatar } from "./badges";
import { toast } from "sonner";
import { MessageCircle, X, ArrowLeft, Send, Plus, Users } from "lucide-react";

function fmtTime(iso) {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function ChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [unread, setUnread] = useState(0);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const bottomRef = useRef(null);

  const loadUnread = useCallback(() => api.get("/chat/unread-count").then((r) => setUnread(r.data.unread)).catch(() => {}), []);
  const loadConvs = useCallback(() => api.get("/chat/conversations").then((r) => setConvs(r.data)).catch(() => {}), []);
  const loadMessages = useCallback((id) =>
    api.get(`/chat/conversations/${id}/messages`).then((r) => { setMessages(r.data); loadUnread(); }).catch(() => {}), [loadUnread]);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 15000);
    return () => clearInterval(t);
  }, [loadUnread]);

  useEffect(() => {
    if (!open) return;
    loadConvs();
    const t = setInterval(() => {
      loadConvs();
      if (active && view === "thread") loadMessages(active.id);
    }, 4000);
    return () => clearInterval(t);
  }, [open, active, view, loadConvs, loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openThread = (c) => {
    setActive(c); setView("thread"); setMessages([]);
    loadMessages(c.id);
  };

  const send = async () => {
    if (!body.trim() || !active) return;
    try {
      await api.post(`/chat/conversations/${active.id}/messages`, { body });
      setBody(""); loadMessages(active.id); loadConvs();
    } catch (err) { toast.error(apiError(err)); }
  };

  const startNew = async () => {
    setView("new"); setSelected([]); setGroupName("");
    if (!users.length) api.get("/users").then((r) => setUsers(r.data.filter((u) => u.id !== user.id)));
  };

  const createConv = async () => {
    try {
      const { data } = await api.post("/chat/conversations", {
        member_ids: selected, name: selected.length > 1 && groupName.trim() ? groupName.trim() : null,
      });
      await loadConvs();
      openThread({ ...data, display_name: data.name || users.filter((u) => selected.includes(u.id)).map((u) => u.name).join(", ") });
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <>
      <button data-testid="chat-widget-btn" onClick={() => { setOpen(!open); if (!open) setView("list"); }}
        className="fixed bottom-5 right-5 z-50 h-13 w-13 p-3.5 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-transform">
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        {!open && unread > 0 && (
          <span data-testid="chat-unread-badge"
            className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-mono flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="chat-panel"
          className="fixed bottom-20 right-5 z-50 w-[370px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[70vh] bg-popover border border-border rounded-lg shadow-2xl flex flex-col overflow-hidden fade-in">
          {view === "list" && (
            <>
              <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
                <p className="font-heading font-semibold text-sm">Messages</p>
                <Button size="sm" variant="outline" data-testid="chat-new-btn" onClick={startNew}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> New chat
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-border">
                {convs.map((c) => (
                  <button key={c.id} data-testid="chat-conversation-item" onClick={() => openThread(c)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-accent/60 transition-colors">
                    {c.member_ids.length > 2 || c.name
                      ? <span className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center shrink-0"><Users className="h-4 w-4 text-white" /></span>
                      : <Avatar name={c.display_name} size="md" />}
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm truncate ${c.unread ? "font-semibold" : "font-medium"}`}>{c.display_name}</p>
                      <p className={`text-xs truncate ${c.unread ? "text-foreground" : "text-muted-foreground"}`}>
                        {c.last_message_sender ? `${c.last_message_sender.split(" ")[0]}: ${c.last_message_preview}` : "No messages yet"}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      {c.last_message_at && <p className="text-[9px] font-mono text-muted-foreground">{fmtTime(c.last_message_at)}</p>}
                      {c.unread > 0 && (
                        <span data-testid="conversation-unread-badge"
                          className="inline-flex mt-1 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-mono items-center justify-center">
                          {c.unread}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
                {!convs.length && <p className="p-6 text-sm text-muted-foreground text-center">No conversations yet. Start one!</p>}
              </div>
            </>
          )}

          {view === "new" && (
            <>
              <div className="px-4 py-3 border-b border-border flex items-center gap-2 shrink-0">
                <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="chat-back-btn" onClick={() => setView("list")}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <p className="font-heading font-semibold text-sm">New conversation</p>
              </div>
              <div className="flex-1 overflow-y-auto">
                {users.map((u) => (
                  <label key={u.id} data-testid={`chat-user-option-${u.email.split("@")[0]}`}
                    className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-accent/50 transition-colors">
                    <Checkbox checked={selected.includes(u.id)}
                      onCheckedChange={(v) => setSelected(v ? [...selected, u.id] : selected.filter((s) => s !== u.id))} />
                    <Avatar name={u.name} size="sm" />
                    <span className="text-sm">{u.name}</span>
                    <span className="text-[9px] font-mono uppercase text-muted-foreground ml-auto">{u.role}</span>
                  </label>
                ))}
              </div>
              <div className="p-3 border-t border-border space-y-2 shrink-0">
                {selected.length > 1 && (
                  <Input data-testid="chat-group-name-input" placeholder="Group name (optional)" value={groupName}
                    onChange={(e) => setGroupName(e.target.value)} className="h-8 text-sm" />
                )}
                <Button className="w-full" size="sm" data-testid="chat-create-btn" disabled={!selected.length} onClick={createConv}>
                  Start chat {selected.length > 0 && `(${selected.length})`}
                </Button>
              </div>
            </>
          )}

          {view === "thread" && active && (
            <>
              <div className="px-4 py-3 border-b border-border flex items-center gap-2 shrink-0">
                <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="chat-thread-back-btn"
                  onClick={() => { setView("list"); setActive(null); loadConvs(); }}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-sm truncate">{active.display_name}</p>
                  <p className="text-[9px] font-mono text-muted-foreground uppercase">{active.member_ids.length} members</p>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex gap-2 ${mine ? "justify-end" : ""}`} data-testid="chat-message-item">
                      {!mine && <Avatar name={m.sender_name} size="xs" />}
                      <div className={`max-w-[75%] rounded-lg px-3 py-2 ${mine ? "bg-primary text-primary-foreground" : "bg-accent"}`}>
                        {!mine && <p className="text-[10px] font-semibold text-primary">{m.sender_name}</p>}
                        <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                        <p className={`text-[9px] font-mono mt-0.5 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                          {fmtTime(m.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                {!messages.length && <p className="text-sm text-muted-foreground text-center py-8">No messages yet. Say hi!</p>}
                <div ref={bottomRef} />
              </div>
              <div className="p-3 border-t border-border flex gap-2 shrink-0">
                <Input data-testid="chat-message-input" placeholder="Type a message..." value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
                <Button size="icon" data-testid="chat-send-btn" onClick={send} disabled={!body.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
